import * as React from "react";
import { useState } from "react";
import axios from "axios";
import {
    Button,
    CircularProgress,
    TextField,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Grid,
    Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import TimelineIcon from "@material-ui/icons/Timeline";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet } from "../getConfig";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: 24,
        backgroundColor: "#f8f9fa",
    },
    header: {
        marginBottom: 24,
    },
    title: {
        fontSize: 24,
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 14,
        color: "#666",
    },
    searchPaper: {
        padding: 20,
        marginBottom: 16,
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    searchRow: {
        display: "flex",
        alignItems: "center",
        gap: 12,
    },
    searchField: {
        width: 360,
        "& .MuiOutlinedInput-root": {
            borderRadius: 6,
        },
    },
    searchButton: {
        height: 40,
        borderRadius: 6,
        textTransform: "none",
        fontWeight: 500,
        paddingLeft: 20,
        paddingRight: 20,
    },
    userCard: {
        padding: 20,
        marginBottom: 16,
        borderRadius: 8,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    userHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 16,
    },
    userBasicInfo: {
        display: "flex",
        flexDirection: "column",
    },
    userName: {
        fontSize: 18,
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 2,
    },
    userEmail: {
        fontSize: 14,
        color: "#666",
        marginBottom: 2,
    },
    userId: {
        fontSize: 12,
        color: "#999",
    },
    referralCode: {
        display: "inline-flex",
        alignItems: "center",
        padding: "8px 16px",
        backgroundColor: "#e8f5e9",
        borderRadius: 6,
        border: "1px dashed #4caf50",
    },
    referralCodeLabel: {
        fontSize: 11,
        color: "#666",
        marginRight: 8,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    referralCodeValue: {
        fontSize: 16,
        fontWeight: 700,
        color: "#2e7d32",
        letterSpacing: 1,
    },
    statsGrid: {
        marginTop: 16,
    },
    statBox: {
        padding: 16,
        borderRadius: 6,
        backgroundColor: "#f8f9fa",
        textAlign: "center" as const,
    },
    statValue: {
        fontSize: 24,
        fontWeight: 700,
        color: "#1a1a1a",
        lineHeight: 1,
        marginBottom: 4,
    },
    statLabel: {
        fontSize: 11,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
    },
    statSuccess: { backgroundColor: "#e8f5e9", "& $statValue": { color: "#2e7d32" } },
    statPending: { backgroundColor: "#fff3e0", "& $statValue": { color: "#e65100" } },
    statFlagged: { backgroundColor: "#ffebee", "& $statValue": { color: "#c62828" } },
    statMoney: { backgroundColor: "#e3f2fd", "& $statValue": { color: "#1565c0" } },
    badgeRow: {
        display: "flex",
        gap: 8,
        marginTop: 12,
    },
    badge: {
        fontSize: 11,
        fontWeight: 600,
        padding: "4px 10px",
        borderRadius: 4,
    },
    tablePaper: {
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
    },
    tableHeader: {
        padding: "16px 20px",
        borderBottom: "1px solid #eee",
    },
    tableTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: "#1a1a1a",
    },
    table: {
        "& .MuiTableCell-head": {
            fontWeight: 600,
            fontSize: 12,
            color: "#666",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            backgroundColor: "#f8f9fa",
            padding: "12px 16px",
        },
        "& .MuiTableCell-body": {
            fontSize: 13,
            padding: "12px 16px",
        },
    },
    clickableRow: {
        cursor: "pointer",
        "&:hover": {
            backgroundColor: "#f8f9fa",
        },
    },
    statusChip: {
        fontSize: 11,
        fontWeight: 600,
        height: 24,
    },
    statusPending: { backgroundColor: "#fff3e0", color: "#e65100" },
    statusQualified: { backgroundColor: "#e3f2fd", color: "#1565c0" },
    statusCompleted: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
    statusRejected: { backgroundColor: "#ffebee", color: "#c62828" },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        padding: 40,
    },
    noData: {
        textAlign: "center" as const,
        padding: 40,
        color: "#999",
    },
    dialogContent: {
        minWidth: 600,
        maxHeight: "70vh",
        padding: 20,
    },
    commissionCard: {
        padding: 16,
        backgroundColor: "#f8f9fa",
        borderRadius: 6,
        marginBottom: 16,
    },
    commissionTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 12,
    },
    commissionGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
        gap: 16,
    },
    commissionItem: {
        textAlign: "center" as const,
    },
    commissionItemLabel: {
        fontSize: 10,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    commissionItemValue: {
        fontSize: 14,
        fontWeight: 600,
        color: "#1a1a1a",
    },
    auditStep: {
        "& .MuiStepLabel-label": {
            fontWeight: 500,
        },
    },
    auditStepContent: {
        paddingLeft: 16,
    },
    auditTime: {
        fontSize: 11,
        color: "#999",
        marginBottom: 4,
    },
    auditData: {
        fontSize: 11,
        backgroundColor: "#f5f5f5",
        padding: 8,
        borderRadius: 4,
        maxHeight: 120,
        overflow: "auto",
        fontFamily: "monospace",
    },
}));

interface Referral {
    id: number;
    referrer_user_id: number;
    referred_user_id: number;
    referral_code: string | null;
    status: "pending" | "qualified" | "completed" | "rejected";
    qualified_at: string | null;
    completed_at: string | null;
    createdAt: string;
    referred_email?: string;
    referred_name?: string;
}

interface AuditLogEntry {
    id: number;
    referral_id: number;
    event_type: string;
    event_data: any;
    source: string;
    previous_state: string | null;
    new_state: string | null;
    error_message: string | null;
    createdAt: string;
}

interface Commission {
    id: number;
    referral_id: number;
    commission_type: string;
    amount: number;
    status: string;
    is_inflight: boolean;
    fraud_status: string;
    config_id: number | null;
    createdAt: string;
}

interface UserData {
    id: number;
    email: string;
    name: string;
    referral_code: string | null;
}

interface UserStats {
    total_referrals: number;
    successful_referrals: number;
    pending_referrals: number;
    flagged_referrals: number;
    paid_out: number;
    inflight_amount: number;
    is_ambassador: boolean;
    is_boost_active: boolean;
    boost_weeks_remaining: number;
}

const REFERRAL_SEARCH_ENDPOINT = API + "/admin/referral-v2-search";
const REFERRAL_AUDIT_ENDPOINT = API + "/referral-v2-audit-log";
const REFERRAL_COMMISSION_ENDPOINT = API + "/referral-v2-commission";

const EVENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
    REFERRAL_CODE_USED: { label: "Referral Code Used", color: "#9c27b0" },
    USER_SIGNUP: { label: "User Signed Up", color: "#2196f3" },
    ONBOARDING_STARTED: { label: "Onboarding Started", color: "#ff9800" },
    ONBOARDING_COMPLETED: { label: "Onboarding Completed", color: "#4caf50" },
    SQS_MESSAGE_QUEUED: { label: "Processing Queued", color: "#607d8b" },
    SQS_MESSAGE_RECEIVED: { label: "Processing Started", color: "#607d8b" },
    FRAUD_CHECK_STARTED: { label: "Fraud Check Started", color: "#ff5722" },
    FRAUD_CHECK_COMPLETED: { label: "Fraud Check Completed", color: "#ff5722" },
    FRAUD_FLAG_DETECTED: { label: "Fraud Flag Detected", color: "#f44336" },
    COMMISSION_CALCULATED: { label: "Commission Calculated", color: "#3f51b5" },
    COMMISSION_CREATED: { label: "Commission Created", color: "#4caf50" },
    INFLIGHT_BALANCE_ADDED: { label: "Balance Added (Inflight)", color: "#00bcd4" },
    BOOST_ACTIVATED: { label: "Boost Activated", color: "#ff9800" },
    BOOST_EXTENDED: { label: "Boost Extended", color: "#ff9800" },
    REFERRAL_STATUS_UPDATED: { label: "Referral Status Updated", color: "#795548" },
    AMBASSADOR_PROMOTED: { label: "Promoted to Ambassador", color: "#e91e63" },
    PAYOUT_INITIATED: { label: "Payout Initiated", color: "#009688" },
    PAYOUT_COMPLETED: { label: "Payout Completed", color: "#4caf50" },
    PAYOUT_FAILED: { label: "Payout Failed", color: "#f44336" },
    ADMIN_REVIEW: { label: "Admin Review", color: "#9e9e9e" },
    ADMIN_APPROVAL: { label: "Admin Approved", color: "#4caf50" },
    ADMIN_REJECTION: { label: "Admin Rejected", color: "#f44336" },
    ERROR: { label: "Error", color: "#f44336" },
};

const ReferralUserTracking = () => {
    const classes = useStyles();
    const notify = useNotify();

    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [referrals, setReferrals] = useState<Referral[]>([]);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [userStats, setUserStats] = useState<UserStats | null>(null);

    const [auditDialogOpen, setAuditDialogOpen] = useState(false);
    const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
    const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
    const [commission, setCommission] = useState<Commission | null>(null);
    const [auditLoading, setAuditLoading] = useState(false);

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            notify("Please enter an email or user ID", "warning");
            return;
        }

        setLoading(true);
        setReferrals([]);
        setUserData(null);
        setUserStats(null);

        try {
            const config = getConfigGet({});
            const response: any = await axios.get(
                `${REFERRAL_SEARCH_ENDPOINT}?query=${encodeURIComponent(searchQuery.trim())}`,
                config
            );

            if (response.data?.status) {
                const data = response.data.data;
                setReferrals(data.referrals || []);
                setUserData(data.user || null);
                setUserStats(data.stats || null);

                if (data.referrals?.length === 0) {
                    notify("No referrals found for this user", "info");
                }
            } else {
                notify(response.data?.message || "Search failed", "warning");
            }
        } catch (error: any) {
            notify(error?.response?.data?.message || "Failed to search referrals", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleReferralClick = async (referral: Referral) => {
        setSelectedReferral(referral);
        setAuditDialogOpen(true);
        setAuditLoading(true);
        setAuditLogs([]);
        setCommission(null);

        try {
            const config = getConfigGet({});

            const auditResponse: any = await axios.get(
                `${REFERRAL_AUDIT_ENDPOINT}?referral_id=${referral.id}&$sort[createdAt]=1`,
                config
            );

            if (auditResponse.data?.data) {
                setAuditLogs(auditResponse.data.data);
            }

            const commissionResponse: any = await axios.get(
                `${REFERRAL_COMMISSION_ENDPOINT}?referral_id=${referral.id}`,
                config
            );

            if (commissionResponse.data?.data?.[0]) {
                setCommission(commissionResponse.data.data[0]);
            }
        } catch (error: any) {
            notify("Failed to load audit trail", "error");
        } finally {
            setAuditLoading(false);
        }
    };

    const getStatusChipClass = (status: string) => {
        switch (status) {
            case "pending": return classes.statusPending;
            case "qualified": return classes.statusQualified;
            case "completed": return classes.statusCompleted;
            case "rejected": return classes.statusRejected;
            default: return "";
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getEventInfo = (eventType: string) => {
        return EVENT_TYPE_LABELS[eventType] || { label: eventType, color: "#9e9e9e" };
    };

    return (
        <div className={classes.main}>
            <div className={classes.header}>
                <Typography className={classes.title}>Referral User Tracking</Typography>
                <Typography className={classes.subtitle}>
                    Search by email or user ID to view referral activity and audit trail
                </Typography>
            </div>

            <Paper className={classes.searchPaper}>
                <div className={classes.searchRow}>
                    <TextField
                        className={classes.searchField}
                        label="Email or User ID"
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                        placeholder="Enter email or user ID"
                    />
                    <Button
                        className={classes.searchButton}
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                    >
                        Search
                    </Button>
                </div>
            </Paper>

            {loading && (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            )}

            {!loading && userData && (
                <>
                    <div className={classes.userCard}>
                        <div className={classes.userHeader}>
                            <div className={classes.userBasicInfo}>
                                <Typography className={classes.userName}>
                                    {userData.name || "Unknown User"}
                                </Typography>
                                <Typography className={classes.userEmail}>{userData.email}</Typography>
                                <Typography className={classes.userId}>User ID: {userData.id}</Typography>
                                {userStats && (
                                    <div className={classes.badgeRow}>
                                        {userStats.is_ambassador && (
                                            <Chip
                                                size="small"
                                                label="Ambassador"
                                                className={classes.badge}
                                                style={{ backgroundColor: "#e91e63", color: "#fff" }}
                                            />
                                        )}
                                        {userStats.is_boost_active && (
                                            <Chip
                                                size="small"
                                                label={`Boost Active (${userStats.boost_weeks_remaining}w left)`}
                                                className={classes.badge}
                                                style={{ backgroundColor: "#ff9800", color: "#fff" }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                            {userData.referral_code && (
                                <div className={classes.referralCode}>
                                    <span className={classes.referralCodeLabel}>Referral Code</span>
                                    <span className={classes.referralCodeValue}>{userData.referral_code}</span>
                                </div>
                            )}
                        </div>

                        {userStats && (
                            <Grid container spacing={2} className={classes.statsGrid}>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={classes.statBox}>
                                        <Typography className={classes.statValue}>{userStats.total_referrals}</Typography>
                                        <Typography className={classes.statLabel}>Total</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statSuccess}`}>
                                        <Typography className={classes.statValue}>{userStats.successful_referrals}</Typography>
                                        <Typography className={classes.statLabel}>Successful</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statPending}`}>
                                        <Typography className={classes.statValue}>{userStats.pending_referrals}</Typography>
                                        <Typography className={classes.statLabel}>Pending</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statFlagged}`}>
                                        <Typography className={classes.statValue}>{userStats.flagged_referrals}</Typography>
                                        <Typography className={classes.statLabel}>Flagged</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statSuccess}`}>
                                        <Typography className={classes.statValue}>£{Number(userStats.paid_out || 0).toFixed(0)}</Typography>
                                        <Typography className={classes.statLabel}>Paid Out</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statMoney}`}>
                                        <Typography className={classes.statValue}>£{Number(userStats.inflight_amount || 0).toFixed(0)}</Typography>
                                        <Typography className={classes.statLabel}>In-flight</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </div>

                    {referrals.length > 0 ? (
                        <Paper className={classes.tablePaper}>
                            <div className={classes.tableHeader}>
                                <Typography className={classes.tableTitle}>
                                    Referrals Made ({referrals.length})
                                </Typography>
                            </div>
                            <TableContainer>
                                <Table className={classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Referred User</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Code Used</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell>Completed</TableCell>
                                            <TableCell align="center">Journey</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {referrals.map((referral) => (
                                            <TableRow
                                                key={referral.id}
                                                className={classes.clickableRow}
                                                onClick={() => handleReferralClick(referral)}
                                            >
                                                <TableCell>{referral.id}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                        <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                        {referral.referred_name || `User #${referral.referred_user_id}`}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{referral.referred_email || "-"}</TableCell>
                                                <TableCell>
                                                    <code style={{ fontSize: 12, color: "#666" }}>
                                                        {referral.referral_code || "-"}
                                                    </code>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={referral.status}
                                                        className={`${classes.statusChip} ${getStatusChipClass(referral.status)}`}
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDate(referral.createdAt)}</TableCell>
                                                <TableCell>{formatDate(referral.completed_at)}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" color="primary">
                                                        <TimelineIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ) : (
                        <Paper className={classes.tablePaper}>
                            <div className={classes.noData}>
                                <Typography>No referrals found for this user</Typography>
                            </div>
                        </Paper>
                    )}
                </>
            )}

            <Dialog open={auditDialogOpen} onClose={() => setAuditDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle style={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <div>
                            <Typography style={{ fontSize: 16, fontWeight: 600 }}>
                                Referral Journey #{selectedReferral?.id}
                            </Typography>
                            <Typography style={{ fontSize: 13, color: "#666" }}>
                                {selectedReferral?.referred_email || `User #${selectedReferral?.referred_user_id}`}
                            </Typography>
                        </div>
                        <IconButton size="small" onClick={() => setAuditDialogOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {auditLoading ? (
                        <div className={classes.loadingContainer}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <>
                            {commission && (
                                <div className={classes.commissionCard}>
                                    <Typography className={classes.commissionTitle}>Commission Details</Typography>
                                    <div className={classes.commissionGrid}>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Amount</Typography>
                                            <Typography className={classes.commissionItemValue}>
                                                £{Number(commission.amount || 0).toFixed(2)}
                                            </Typography>
                                        </div>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Type</Typography>
                                            <Typography className={classes.commissionItemValue}>
                                                {commission.commission_type}
                                            </Typography>
                                        </div>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Status</Typography>
                                            <Chip
                                                size="small"
                                                label={commission.status}
                                                style={{
                                                    backgroundColor: commission.status === "completed" ? "#e8f5e9" : "#fff3e0",
                                                    color: commission.status === "completed" ? "#2e7d32" : "#e65100",
                                                    fontSize: 11,
                                                    height: 22,
                                                }}
                                            />
                                        </div>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Fraud</Typography>
                                            <Chip
                                                size="small"
                                                label={commission.fraud_status}
                                                style={{
                                                    backgroundColor: commission.fraud_status === "clean" ? "#e8f5e9" : "#ffebee",
                                                    color: commission.fraud_status === "clean" ? "#2e7d32" : "#c62828",
                                                    fontSize: 11,
                                                    height: 22,
                                                }}
                                            />
                                        </div>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Inflight</Typography>
                                            <Typography className={classes.commissionItemValue}>
                                                {commission.is_inflight ? "Yes" : "No"}
                                            </Typography>
                                        </div>
                                        {commission.config_id && (
                                            <div className={classes.commissionItem}>
                                                <Typography className={classes.commissionItemLabel}>Campaign</Typography>
                                                <Typography className={classes.commissionItemValue}>
                                                    #{commission.config_id}
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {auditLogs.length > 0 ? (
                                <Stepper orientation="vertical" style={{ padding: 0 }}>
                                    {auditLogs.map((log) => {
                                        const eventInfo = getEventInfo(log.event_type);
                                        return (
                                            <Step key={log.id} active expanded className={classes.auditStep}>
                                                <StepLabel
                                                    StepIconComponent={() => (
                                                        <div
                                                            style={{
                                                                width: 22,
                                                                height: 22,
                                                                borderRadius: "50%",
                                                                backgroundColor: eventInfo.color,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            {log.event_type.includes("ERROR") ? (
                                                                <ErrorIcon style={{ color: "#fff", fontSize: 14 }} />
                                                            ) : log.event_type.includes("COMPLETED") || log.event_type.includes("CREATED") ? (
                                                                <CheckCircleIcon style={{ color: "#fff", fontSize: 14 }} />
                                                            ) : (
                                                                <HourglassEmptyIcon style={{ color: "#fff", fontSize: 14 }} />
                                                            )}
                                                        </div>
                                                    )}
                                                >
                                                    <span style={{ color: eventInfo.color, fontWeight: 600, fontSize: 13 }}>
                                                        {eventInfo.label}
                                                    </span>
                                                </StepLabel>
                                                <StepContent className={classes.auditStepContent}>
                                                    <div className={classes.auditTime}>
                                                        {formatDate(log.createdAt)}
                                                        {log.source && <span> · {log.source}</span>}
                                                    </div>
                                                    {log.previous_state && log.new_state && (
                                                        <Typography style={{ fontSize: 12, marginBottom: 4 }}>
                                                            {log.previous_state} → {log.new_state}
                                                        </Typography>
                                                    )}
                                                    {log.error_message && (
                                                        <Typography style={{ fontSize: 12, color: "#c62828", marginBottom: 4 }}>
                                                            {log.error_message}
                                                        </Typography>
                                                    )}
                                                    {log.event_data && Object.keys(log.event_data).length > 0 && (
                                                        <div className={classes.auditData}>
                                                            <pre style={{ margin: 0 }}>
                                                                {JSON.stringify(log.event_data, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </StepContent>
                                            </Step>
                                        );
                                    })}
                                </Stepper>
                            ) : (
                                <div className={classes.noData}>
                                    <Typography>No audit trail found</Typography>
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Notification />
        </div>
    );
};

export default ReferralUserTracking;
